import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import type { ColumnDef } from "@tanstack/react-table";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Icon from "@/components/Icon";
import SectionHeading from "@/components/SectionHeading";
import IconButton from "@/components/IconButton";
import Table, { buildCopyTextColumn, buildIconTextColumn } from "@/components/Table";
import type { IconConfig } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import Tooltip from "@/components/Tooltip";
import { toFormat } from "@/lib/dateHelpers";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "../mutations";
import type { ApplicationEmploymentType } from "../mutations";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import type { ApplicationDocument, ApplicationEmploymentDetails, EligibilityCheck } from "../queries";
import { canEditApplicationFields, hasEligibility } from "../utils";
import UpdateEmploymentClassificationModal from "./UpdateEmploymentClassificationModal";

interface EmploymentRow {
  id: string;
  employmentType: ApplicationEmploymentType;
  facilityId: string | null;
  employer: string | null;
  sectionName: string | null;
  jobTitle: string | null;
  jobClassification: string | null;
  workingLocation: ApplicationEmploymentDetails["workingLocation"];
  affiliationSectionName: string | null;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  jobDescription: string | null;
}

function formatEmploymentDate(date: string | null | undefined): string {
  if (!date) return "—";
  return toFormat(date) || "—";
}

function formatWorkingLocation(value: ApplicationEmploymentDetails["workingLocation"]): string {
  if (!value) return "—";
  switch (value) {
    case "ON_SITE":
      return "On-site";
    case "REMOTE":
      return "Remote";
    default:
      return "—";
  }
}

function buildEmploymentRows(
  currentEmployment: ApplicationEmploymentDetails | null,
  previousEmployment: ApplicationEmploymentDetails | null
): EmploymentRow[] {
  const rows: EmploymentRow[] = [];

  if (currentEmployment) {
    rows.push({
      id: "current",
      employmentType: "CURRENT",
      facilityId: currentEmployment.facilityId,
      employer: currentEmployment.employer,
      sectionName: currentEmployment.sectionName,
      jobTitle: currentEmployment.jobTitle,
      jobClassification: currentEmployment.jobClassification,
      workingLocation: currentEmployment.workingLocation,
      affiliationSectionName: currentEmployment.affiliationSection?.name ?? null,
      startDate: currentEmployment.startDate,
      endDate: null,
      isCurrent: true,
      jobDescription: currentEmployment.jobDescription
    });
  }

  if (previousEmployment) {
    rows.push({
      id: "previous",
      employmentType: "PREVIOUS",
      facilityId: previousEmployment.facilityId,
      employer: previousEmployment.employer,
      sectionName: previousEmployment.sectionName,
      jobTitle: previousEmployment.jobTitle,
      jobClassification: previousEmployment.jobClassification,
      workingLocation: previousEmployment.workingLocation,
      affiliationSectionName: previousEmployment.affiliationSection?.name ?? null,
      startDate: previousEmployment.startDate,
      endDate: previousEmployment.endDate,
      isCurrent: false,
      jobDescription: previousEmployment.jobDescription
    });
  }

  return rows;
}

const missingClassificationWarning = {
  name: "emergency_home" as const,
  size: 24,
  className: "text-extended-warning-brand-color",
  tooltip: {
    supportingText: "Classification needs to be set",
    menuCorner: "end-start" as const,
    anchorCorner: "start-start" as const
  }
};

function JobDescriptionDocumentLinks({ documents }: { documents: ApplicationDocument[] }) {
  if (documents.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-1">
      {documents.map((document) => (
        <a
          key={document.id}
          href={document.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sys-brand-primary hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          {document.filename}
        </a>
      ))}
    </div>
  );
}

function getEmploymentJobTitleIconConfig(row: EmploymentRow, documents: ApplicationDocument[]): IconConfig | null {
  const hasDescription = !!row.jobDescription;
  const includeDocuments = row.isCurrent && documents.length > 0;
  if (!hasDescription && !includeDocuments) return null;

  return {
    name: "description",
    size: 24,
    className: "text-sys-brand-on-surface-variant",
    tooltip: {
      title: "Job Description",
      variant: "rich",
      color: "white",
      positioning: "fixed",
      anchorCorner: "end-start",
      menuCorner: "start-end",
      closeDelay: 500,
      supportingText: (
        <div className="flex w-full flex-col gap-2 text-left text-sys-brand-on-surface-variant">
          {hasDescription && <div className="w-full whitespace-pre-wrap">{row.jobDescription}</div>}
          {includeDocuments && <JobDescriptionDocumentLinks documents={documents} />}
        </div>
      )
    }
  };
}

interface Props {
  applicationId: string;
  status: string;
  currentEmploymentDetails: ApplicationEmploymentDetails | null;
  previousEmploymentDetails: ApplicationEmploymentDetails | null;
  jobDescriptionDocuments?: ApplicationDocument[] | null;
  eligibilityStatuses: EligibilityCheck[];
  onViewFacility?: (facilityId: string) => void;
}

export default function AssociateEmploymentSection({
  applicationId,
  status,
  currentEmploymentDetails,
  previousEmploymentDetails,
  jobDescriptionDocuments,
  eligibilityStatuses,
  onViewFacility
}: Props) {
  const rows = buildEmploymentRows(currentEmploymentDetails, previousEmploymentDetails);
  const documents = useMemo(() => jobDescriptionDocuments ?? [], [jobDescriptionDocuments]);
  const allRowsHaveClassification = rows.every((row) => !!row.jobClassification);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [classificationModalRow, setClassificationModalRow] = useState<EmploymentRow | null>(null);
  const [deletePreviousEmploymentOpen, setDeletePreviousEmploymentOpen] = useState(false);
  const [employmentRowToDelete, setEmploymentRowToDelete] = useState<EmploymentRow | null>(null);
  const { setError, clearError } = useErrorBanner();
  const canEdit = canEditApplicationFields({ status });
  const isEmploymentEligible = hasEligibility(eligibilityStatuses, "EMPLOYMENT", "ELIGIBLE");

  const [updateApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data) => {
      if (data.updateSubmittedApplication.success) {
        clearError();
        showSnackbar(data.updateSubmittedApplication.message);
        setDialogOpen(false);
      } else {
        setError(data.updateSubmittedApplication.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const columns = useMemo<ColumnDef<EmploymentRow, unknown>[]>(
    () => [
      buildCopyTextColumn<EmploymentRow>({
        accessorKey: "employer",
        header: "Facility Name",
        copyFieldLabel: "Facility Name",
        size: 250,
        enableSorting: false,
        getText: (row) => row.employer ?? "",
        getSecondaryText: (row) => row.sectionName ?? undefined,
        shouldShowCopyButton: (row) => !!row.employer,
        getPrefix: (row) =>
          row.isCurrent ? (
            <Tooltip variant="plain" supportingText="Primary" color="black">
              <Icon name="check" size={24} />
            </Tooltip>
          ) : null,
        meta: { headerClassName: "hide-header-in-column" }
      }),
      buildIconTextColumn<EmploymentRow>({
        accessorKey: "jobClassification",
        header: "Employed As",
        size: 140,
        enableSorting: false,
        meta: { skipEmptyState: true },
        getText: (row) => row.jobClassification ?? "",
        getIcon: (row) => (row.jobClassification ? null : missingClassificationWarning)
      }),
      buildIconTextColumn<EmploymentRow>({
        accessorKey: "jobTitle",
        header: "Job Title",
        size: 200,
        enableSorting: false,
        meta: { skipEmptyState: true },
        getText: (row) => row.jobTitle ?? "",
        getIcon: (row) => getEmploymentJobTitleIconConfig(row, documents)
      }),
      {
        id: "workingLocation",
        header: "Working Location",
        accessorFn: (row) => row.workingLocation,
        cell: ({ row }) => {
          const locationLabel = formatWorkingLocation(row.original.workingLocation);
          if (row.original.workingLocation === "REMOTE" && row.original.affiliationSectionName) {
            return renderEmptyCellState(`${locationLabel} · ${row.original.affiliationSectionName}`);
          }
          return renderEmptyCellState(locationLabel);
        },
        size: 200,
        enableSorting: false
      },
      {
        id: "startDate",
        header: "Start Date",
        accessorFn: (row) => row.startDate,
        cell: ({ row }) => renderEmptyCellState(formatEmploymentDate(row.original.startDate)),
        size: 140,
        enableSorting: false
      },
      {
        id: "endDate",
        header: "End Date",
        accessorFn: (row) => row.endDate,
        cell: ({ row }) =>
          renderEmptyCellState(row.original.isCurrent ? "Present" : formatEmploymentDate(row.original.endDate)),
        size: 140,
        enableSorting: false
      },
      {
        id: "actions",
        header: () => null,
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            {row.original.facilityId && onViewFacility && (
              <IconButton
                data-testid="expand-details"
                title="View Facility"
                name="open_in_full"
                iconClassName="text-sys-surface-variant"
                className="!m-0 !p-0"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onViewFacility(row.original.facilityId!);
                }}
              />
            )}
            {canEdit && (
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setClassificationModalRow(row.original);
                }}
                data-testid="edit-action"
                title="Update Employment"
                name="edit"
                className="!m-0 !p-0 h-10"
              />
            )}
            {canEdit && !row.original.isCurrent && (
              <IconButton
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setEmploymentRowToDelete(row.original);
                  setDeletePreviousEmploymentOpen(true);
                }}
                data-testid="delete-employment-action"
                title="Delete Employment"
                name="delete"
                iconClassName="text-extended-error-brand-color"
                className="!m-0 !p-0 h-10"
              />
            )}
          </div>
        ),
        meta: { skipEmptyState: true }
      }
    ],
    [canEdit, documents, onViewFacility]
  );

  const handleToggle = () => {
    updateApplication({
      variables: {
        input: {
          id: applicationId,
          eligibility: {
            type: "EMPLOYMENT",
            status: isEmploymentEligible ? "INELIGIBLE" : "ELIGIBLE"
          }
        }
      }
    });
  };

  const handleDeleteApplicationEmployment = () => {
    if (!employmentRowToDelete) return;

    updateApplication({
      variables: {
        input: {
          id: applicationId,
          deleteApplicationEmployment: {
            employmentType: employmentRowToDelete.employmentType,
            facilityId: employmentRowToDelete.facilityId
          }
        }
      },
      onCompleted: (data) => {
        if (data.updateSubmittedApplication.success) {
          setDeletePreviousEmploymentOpen(false);
          setEmploymentRowToDelete(null);
        }
      }
    });
  };

  return (
    <>
      <div className="flex flex-row justify-between w-full items-center">
        <SectionHeading
          heading="Employment History"
          body="View employment history including past and present facilities and job titles below"
        />
        <div className="pt-8">
          {canEdit &&
            (isEmploymentEligible ? (
              <Button
                variant="outlined"
                icon="undo"
                onClick={() => setDialogOpen(true)}
                testId="employment-history-open-eligibility-dialog"
              >
                Unconfirm Eligibility
              </Button>
            ) : (
              <Button
                variant="outlined"
                icon="task"
                onClick={() => setDialogOpen(true)}
                disabled={!allRowsHaveClassification}
                testId="employment-history-open-eligibility-dialog"
              >
                Confirm Eligibility
              </Button>
            ))}
        </div>
      </div>
      <Table<EmploymentRow> loading={false} data={rows} columns={columns} getRowId={(row) => row.id} />
      {rows.length === 0 && documents.length > 0 && (
        <div className="mt-2" data-testid="employment-history-job-description-documents">
          <p className="mb-1 text-body-medium text-sys-brand-on-surface-variant">Job description documents</p>
          <JobDescriptionDocumentLinks documents={documents} />
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onClosed={() => setDialogOpen(false)}
        headline={isEmploymentEligible ? "Unconfirm Eligibility" : "Confirm Eligibility"}
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setDialogOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleToggle}
              disabled={loading}
              testId="employment-history-eligibility-dialog-submit"
            >
              {isEmploymentEligible ? "Unconfirm Eligibility" : "Confirm Eligibility"}
            </Button>
          </div>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          {isEmploymentEligible
            ? "Are you sure you want to unconfirm this applicant's employment history review? This will mark the verification step as incomplete."
            : "Are you sure you want to confirm this applicant's employment history review? Confirmation will mark the application verification step as completed. Once all steps have been completed, the application can be approved."}
        </p>
      </Dialog>

      <Dialog
        open={deletePreviousEmploymentOpen}
        onClosed={() => {
          setDeletePreviousEmploymentOpen(false);
          setEmploymentRowToDelete(null);
        }}
        headline="Delete Employment"
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setDeletePreviousEmploymentOpen(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              icon="delete"
              onClick={handleDeleteApplicationEmployment}
              disabled={loading || !employmentRowToDelete}
              testId="confirm-delete-employment"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Are you sure you want to delete this employment? This cannot be undone.
        </p>
      </Dialog>

      {canEdit && classificationModalRow && (
        <UpdateEmploymentClassificationModal
          open
          onClose={() => setClassificationModalRow(null)}
          applicationId={applicationId}
          employmentType={classificationModalRow.employmentType}
          employer={classificationModalRow.employer}
          facilityId={classificationModalRow.facilityId}
          jobTitle={classificationModalRow.jobTitle}
          jobClassification={classificationModalRow.jobClassification}
          workingLocation={classificationModalRow.workingLocation}
          startDate={classificationModalRow.startDate}
        />
      )}
    </>
  );
}
